// <repeating-rows> — nested-attributes row management. Markup convention:
//
//   <repeating-rows index-placeholder="__INDEX__">
//     <template data-repeating-rows-template>   ← one blank row, __INDEX__ in names
//     <div data-repeating-rows-container>        ← existing rows live here
//       <div data-repeating-rows-row>
//         ...fields, hidden [_destroy] if persisted...
//         <button data-repeating-rows-remove>
//     <button data-repeating-rows-add>
//
// Remove semantics: a row containing a [_destroy] input is persisted — set it
// to "1" and hide the row so Rails deletes it on save; a row without one is
// unsaved — just remove it from the DOM. New rows are stamped with Date.now()
// so indexes can never collide with server-rendered ones.

class RepeatingRows extends HTMLElement {
	#abort?: AbortController;

	connectedCallback() {
		this.#abort?.abort();
		this.#abort = new AbortController();
		this.addEventListener("click", (event) => this.#click(event), {
			signal: this.#abort.signal,
		});
	}

	disconnectedCallback() {
		this.#abort?.abort();
	}

	#click(event: Event) {
		const target = event.target as HTMLElement;

		const add = target.closest<HTMLElement>("[data-repeating-rows-add]");
		if (add && this.#owns(add)) {
			event.preventDefault();
			this.#addRow();
			return;
		}

		const remove = target.closest<HTMLElement>("[data-repeating-rows-remove]");
		if (remove && this.#owns(remove)) {
			event.preventDefault();
			this.#removeRow(remove);
		}
	}

	// Nested <repeating-rows> (the rules editor: groups containing condition
	// rows) must not capture each other's buttons.
	#owns(el: HTMLElement) {
		return el.closest("repeating-rows") === this;
	}

	#addRow() {
		const template = this.querySelector<HTMLTemplateElement>(
			"template[data-repeating-rows-template]",
		);
		const container = this.querySelector<HTMLElement>(
			"[data-repeating-rows-container]",
		);
		if (!template || !container) return;

		const placeholder = this.getAttribute("index-placeholder") ?? "__INDEX__";
		const html = template.innerHTML.replaceAll(placeholder, String(Date.now()));
		container.insertAdjacentHTML("beforeend", html);
		this.dispatchEvent(
			new CustomEvent("repeating-rows:added", { bubbles: true }),
		);
	}

	#removeRow(button: HTMLElement) {
		const row = button.closest<HTMLElement>("[data-repeating-rows-row]");
		if (!row) return;

		const destroy = row.querySelector<HTMLInputElement>(
			"input[name$='[_destroy]']",
		);
		if (destroy) {
			destroy.value = "1";
			row.hidden = true;
		} else {
			row.remove();
		}
		this.dispatchEvent(
			new CustomEvent("repeating-rows:removed", { bubbles: true }),
		);
	}
}

if (!customElements.get("repeating-rows")) {
	customElements.define("repeating-rows", RepeatingRows);
}
